import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Briefcase, DollarSign, Users, Calendar, Phone, Mail, Building2, Clock, ArrowLeft, Lock, UserPlus, GraduationCap, AlertTriangle, CheckSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api, { getAssetUrl, formatSalary } from '@/utils/api';
import { getUser, isAuthenticated } from '@/utils/auth';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = getUser();
  const isJobSeeker = isAuthenticated() && user?.role === 'job_seeker';
  
  // Profile completion state
  const [profileCompleted, setProfileCompleted] = useState(true);
  const [profileStrength, setProfileStrength] = useState(0);
  const [profileMessage, setProfileMessage] = useState('');
  const [missingSections, setMissingSections] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  
  // Declaration modal state
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [declarations, setDeclarations] = useState({
    infoTrue: false,
    meetsRequirements: false,
    understandsTerms: false,
    agreesVerification: false
  });
  const [applyLoading, setApplyLoading] = useState(false);
  
  // Check if this is a Prime Employer job
  const isPrimeEmployerJob = job?.is_prime_employer_job === true;

  const fetchJobDetails = async () => {
    try {
      // Pass user_id for prime employer jobs to get contact details
      const url = user?.id ? `/jobs/${jobId}?user_id=${user.id}` : `/jobs/${jobId}`;
      const response = await api.get(url);
      setJob(response.data);
    } catch (error) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  // Check profile status for job seekers
  const checkProfileStatus = async () => {
    if (!isJobSeeker || !user?.id) return;
    
    try {
      const response = await api.get(`/job-seeker/profile-status/${user.id}`);
      setProfileCompleted(response.data.profile_completed);
      setProfileStrength(response.data.profile_strength || 0);
      setProfileMessage(response.data.message || '');
      setMissingSections(response.data.missing_sections || []);
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Error checking profile status:', error);
    }
  };

  useEffect(() => {
    fetchJobDetails();
    checkProfileStatus();
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open declaration modal before applying
  const openApplyModal = () => {
    if (!isAuthenticated() || !user || user.role !== 'job_seeker') {
      toast.error('Please login as Job Seeker to apply');
      navigate('/job-seeker/login');
      return;
    }

    // Check profile completion before showing declaration
    if (!profileCompleted) {
      toast.error('Please complete your profile before applying', {
        description: profileMessage,
        duration: 5000,
        action: {
          label: 'Complete Profile',
          onClick: () => navigate('/job-seeker/profile')
        }
      });
      return;
    }
    
    // Reset declarations and show modal
    setDeclarations({
      infoTrue: false,
      meetsRequirements: false,
      understandsTerms: false,
      agreesVerification: false
    });
    setShowDeclarationModal(true);
  };

  // Check if all declarations are checked
  const allDeclarationsChecked = declarations.infoTrue && 
    declarations.meetsRequirements && 
    declarations.understandsTerms && 
    declarations.agreesVerification;

  // Submit application after declarations
  const handleApply = async () => {
    if (!allDeclarationsChecked) {
      toast.error('Please agree to all declarations before applying');
      return;
    }

    setApplyLoading(true);
    try {
      await api.post('/job-seeker/apply', {
        job_id: jobId,
        job_seeker_id: user.id,
        declarations_accepted: true,
        declaration_timestamp: new Date().toISOString()
      });
      toast.success('Application submitted successfully!');
      setShowDeclarationModal(false);
      navigate('/job-seeker/dashboard');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMsg = Array.isArray(errorDetail) 
        ? errorDetail.map(e => e.msg || e.message).join(', ')
        : (typeof errorDetail === 'object' ? errorDetail.msg || errorDetail.message : errorDetail);
      
      // Check if error is due to incomplete profile
      if (errorMsg?.includes('profile') || errorMsg?.includes('Missing')) {
        toast.error(errorMsg, {
          action: {
            label: 'Complete Profile',
            onClick: () => navigate('/job-seeker/profile')
          }
        });
      } else {
        toast.error(errorMsg || 'Failed to apply');
      }
    } finally {
      setApplyLoading(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return 'https://images.pexels.com/photos/345132/pexels-photo-345132.jpeg?auto=compress&cs=tinysrgb&w=150&h=150';
    if (path.startsWith('http')) return path;
    return getAssetUrl(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading job details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Job Not Found</h2>
            <Button onClick={() => navigate('/')} className="mt-4">Back to Home</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Expired job — show clear notification with CTA to browse other jobs
  if (job.is_expired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-slate-50 to-white flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div data-testid="job-expired-notice" className="max-w-xl w-full text-center bg-white rounded-2xl shadow-sm border border-slate-200 p-10">
            <div className="w-20 h-20 mx-auto rounded-full bg-rose-100 flex items-center justify-center mb-5">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">This job opening has expired</h1>
            <p className="text-slate-600 mb-1">
              <span className="font-semibold">{job.job_title}</span>
              {job.company_name ? ` at ${job.company_name}` : ''}
            </p>
            <p className="text-sm text-slate-500 mb-8">
              Application closed on {job.last_date ? new Date(job.last_date).toLocaleDateString('en-GB') : '—'}.
              Please explore other active opportunities below.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                data-testid="browse-jobs-btn"
                onClick={() => navigate('/jobs')}
                className="bg-teal-600 hover:bg-teal-700 text-white px-6"
              >
                Browse Active Jobs
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="px-6"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Check if profile is complete for showing contact info
  const canViewContactInfo = isJobSeeker && profileCompleted;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-white flex flex-col">
      <SEO 
        title={`${job.job_title} at ${job.company_name} | VVVSI Jobs`}
        description={`Apply for ${job.job_title} position at ${job.company_name}. Salary: ${formatSalary(job)}. Experience: ${job.min_experience}-${job.max_experience} years. Apply now on VVVSI Job Portal.`}
        keywords={`${job.job_title}, ${job.company_name}, jobs, ${job.locations?.map(l => l.district || l.city).join(', ')}, career, employment`}
        url={`/job/${jobId}`}
        type="article"
      />
      <Header />
      
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button 
            data-testid="back-to-home-btn"
            variant="ghost" 
            onClick={() => navigate('/')} 
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Jobs
          </Button>

          {/* Profile Incomplete Alert for logged-in job seekers */}
          {isJobSeeker && !profileCompleted && (
            <Alert className="mb-6 border-amber-300 bg-amber-50">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <div className="font-semibold mb-2">
                  Profile {profileStrength}% Complete - Minimum 50% Required to Apply
                </div>
                <div className="mb-3">
                  {/* Progress bar */}
                  <div className="w-full bg-amber-200 rounded-full h-2.5 mb-2">
                    <div 
                      className="bg-amber-600 h-2.5 rounded-full transition-all duration-300" 
                      style={{ width: `${profileStrength}%` }}
                    ></div>
                  </div>
                  <span className="text-sm">{50 - profileStrength}% more needed to apply</span>
                </div>
                {missingSections.length > 0 && (
                  <div className="mb-2 text-sm">
                    <strong>Complete these sections:</strong>
                    <ul className="list-disc list-inside ml-2 mt-1">
                      {missingSections.map((section, i) => (
                        <li key={i}>{section}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {suggestions.length > 0 && (
                  <div className="text-sm text-amber-700 mb-2">
                    <strong>Quick tips:</strong> {suggestions.slice(0, 3).join(' • ')}
                  </div>
                )}
                <Button 
                  className="bg-amber-600 hover:bg-amber-700 text-white mt-2"
                  onClick={() => navigate('/job-seeker/profile')}
                >
                  Complete Profile Now →
                </Button>
              </AlertDescription>
            </Alert>
          )}

          <Card className="p-8 shadow-lg border-slate-200">
            <div className="flex gap-6 mb-6">
              <img 
                src={getImageUrl(job.company_logo)} 
                alt={job.company_name}
                className="w-24 h-24 rounded-lg object-cover border border-slate-200"
                onError={(e) => { e.target.src = 'https://images.pexels.com/photos/345132/pexels-photo-345132.jpeg?auto=compress&cs=tinysrgb&w=150&h=150'; }}
              />
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
                  {job.job_title}
                </h1>
                <p className="text-lg text-slate-600 flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5" />
                  {job.company_name}
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-1 text-slate-700">
                    <MapPin className="w-4 h-4" />
                    {job.locations?.map(loc => loc.city).join(', ') || 'Not specified'}
                  </span>
                  <span className="flex items-center gap-1 text-slate-700">
                    <Briefcase className="w-4 h-4" />
                    {job.min_experience}-{job.max_experience} years
                  </span>
                  <span className="flex items-center gap-1 text-slate-700">
                    <DollarSign className="w-4 h-4" />
                    {formatSalary(job)}
                  </span>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                    {job.job_type}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-slate-600" />
                <span className="text-slate-700"><strong>Vacancies:</strong> {job.vacancies}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-slate-600" />
                <span className="text-slate-700"><strong>Last Date:</strong> {new Date(job.last_date).toLocaleDateString('en-IN')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-slate-600" />
                <span className="text-slate-700"><strong>Posted:</strong> {new Date(job.created_at).toLocaleDateString('en-IN')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-700"><strong>Gender:</strong> {job.gender?.join(', ') || 'All'}</span>
              </div>
              {(job.min_education || job.required_education) && (
                <div className="flex items-center gap-2 text-sm">
                  <GraduationCap className="w-4 h-4 text-slate-600" />
                  <span className="text-slate-700"><strong>Required Education:</strong> {job.required_education || job.min_education}</span>
                </div>
              )}
              {job.course_subject && (
                <div className="flex items-center gap-2 text-sm">
                  <GraduationCap className="w-4 h-4 text-slate-600" />
                  <span className="text-slate-700"><strong>Course/Subject:</strong> {job.course_subject}</span>
                </div>
              )}
              {(job.min_age || job.max_age) && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-700"><strong>Age Limit:</strong> {
                    job.min_age && job.max_age 
                      ? `${job.min_age} - ${job.max_age} years`
                      : job.min_age 
                        ? `Min ${job.min_age} years`
                        : `Max ${job.max_age} years`
                  }</span>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Outfit'}}>Job Description</h2>
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{job.description}</p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Outfit'}}>Short Info</h2>
                <p className="text-slate-700 leading-relaxed">{job.short_info}</p>
              </div>

              {/* Skills Section */}
              {job.skills && job.skills.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Outfit'}}>Required Skills</h2>
                  <div className="flex flex-wrap gap-2">
                    {job.skills.map((skill, index) => (
                      <span key={index} className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Details Section */}
              <div className="border-t border-slate-200 pt-6">
                <h2 className="text-xl font-semibold text-slate-900 mb-3" style={{fontFamily: 'Outfit'}}>Contact Details</h2>
                
                {canViewContactInfo ? (
                  // Show contact details only for logged-in job seekers with complete profile
                  <div className="space-y-2">
                    {job.contact_number && (
                      <p className="flex items-center gap-2 text-slate-700">
                        <Phone className="w-4 h-4 text-teal-600" />
                        {job.contact_number}
                      </p>
                    )}
                    {job.email && (
                      <p className="flex items-center gap-2 text-slate-700">
                        <Mail className="w-4 h-4 text-teal-600" />
                        {job.email}
                      </p>
                    )}
                  </div>
                ) : isJobSeeker && !profileCompleted ? (
                  // Job seeker logged in but profile incomplete
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-6 h-6 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">
                          Complete Your Profile to View Contact Details
                        </h3>
                        <p className="text-slate-600 text-sm mb-4">
                          Please add your <strong>Gender</strong> and <strong>Education</strong> details to view employer contact information and apply for jobs.
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <Button 
                            data-testid="complete-profile-btn"
                            onClick={() => navigate('/job-seeker/profile')}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Complete Profile
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Blurred contact preview */}
                    <div className="mt-4 pt-4 border-t border-amber-200">
                      <div className="flex flex-col gap-2 filter blur-sm select-none">
                        <p className="flex items-center gap-2 text-slate-500">
                          <Phone className="w-4 h-4" />
                          +91 XXXXX XXXXX
                        </p>
                        <p className="flex items-center gap-2 text-slate-500">
                          <Mail className="w-4 h-4" />
                          hr@xxxxxxx.com
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Not logged in
                  <div className="bg-gradient-to-r from-teal-50 to-slate-50 border border-teal-200 rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Lock className="w-6 h-6 text-teal-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">
                          Login to View Contact Details
                        </h3>
                        <p className="text-slate-600 text-sm mb-4">
                          Register or login as a Job Seeker to view the employer&apos;s contact information and apply for this job directly.
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <Button 
                            data-testid="login-to-view-btn"
                            onClick={() => navigate('/job-seeker/login')}
                            className="bg-teal-600 hover:bg-teal-700 text-white"
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Login / Register
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Blurred contact preview */}
                    <div className="mt-4 pt-4 border-t border-teal-200">
                      <div className="flex flex-col gap-2 filter blur-sm select-none">
                        <p className="flex items-center gap-2 text-slate-500">
                          <Phone className="w-4 h-4" />
                          +91 XXXXX XXXXX
                        </p>
                        <p className="flex items-center gap-2 text-slate-500">
                          <Mail className="w-4 h-4" />
                          hr@xxxxxxx.com
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Apply Button Section */}
              <div className="flex gap-4 pt-6">
                {isPrimeEmployerJob ? (
                  // Prime Employer jobs - No Apply button, just show contact info message
                  <div className="flex-1">
                    {canViewContactInfo ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                        <p className="text-amber-800 font-medium">
                          To apply for this job, please contact the employer directly using the contact details above.
                        </p>
                      </div>
                    ) : isJobSeeker && !profileCompleted ? (
                      <Button 
                        data-testid="complete-profile-to-apply-btn"
                        onClick={() => navigate('/job-seeker/profile')} 
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12 text-lg"
                      >
                        <AlertTriangle className="w-5 h-5 mr-2" />
                        Complete Profile to Apply
                      </Button>
                    ) : (
                      <Button 
                        data-testid="login-to-apply-btn"
                        onClick={() => navigate('/job-seeker/login')} 
                        className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-lg"
                      >
                        <UserPlus className="w-5 h-5 mr-2" />
                        Login to View Contact Details
                      </Button>
                    )}
                  </div>
                ) : canViewContactInfo ? (
                  <Button 
                    data-testid="apply-job-btn"
                    onClick={openApplyModal} 
                    className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-12 text-lg"
                  >
                    Apply Now
                  </Button>
                ) : isJobSeeker && !profileCompleted ? (
                  <Button 
                    data-testid="complete-profile-to-apply-btn"
                    onClick={() => navigate('/job-seeker/profile')} 
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-12 text-lg"
                  >
                    <AlertTriangle className="w-5 h-5 mr-2" />
                    Complete Profile to Apply
                  </Button>
                ) : (
                  <Button 
                    data-testid="login-to-apply-btn"
                    onClick={() => navigate('/job-seeker/login')} 
                    className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-12 text-lg"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    Login to Apply
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Why Register Section */}
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>
              Why Register with Us?
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="p-4 border-slate-200">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-3">
                  <Briefcase className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Access All Jobs</h3>
                <p className="text-sm text-slate-600">Get access to thousands of job opportunities from top employers.</p>
              </Card>
              <Card className="p-4 border-slate-200">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-3">
                  <Users className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Direct Contact</h3>
                <p className="text-sm text-slate-600">Connect directly with employers and HR managers.</p>
              </Card>
              <Card className="p-4 border-slate-200">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-3">
                  <Mail className="w-5 h-5 text-teal-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Job Alerts</h3>
                <p className="text-sm text-slate-600">Get notified about new jobs matching your profile.</p>
              </Card>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
      
      {/* Declaration Modal for Job Application */}
      <Dialog open={showDeclarationModal} onOpenChange={setShowDeclarationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <CheckSquare className="w-6 h-6 text-teal-600" />
              Application Declaration
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Please read and agree to the following declarations before submitting your application:
            </p>
            
            <div className="space-y-4">
              {/* Declaration 1 */}
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-50 border border-slate-200">
                <Checkbox
                  id="infoTrue"
                  checked={declarations.infoTrue}
                  onCheckedChange={(checked) => setDeclarations({...declarations, infoTrue: checked})}
                  className="mt-0.5"
                />
                <span className="text-sm text-slate-700 leading-relaxed">
                  I declare that all the information provided in my profile and this application is <strong>true and correct</strong> to the best of my knowledge.
                </span>
              </label>
              
              {/* Declaration 2 */}
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-50 border border-slate-200">
                <Checkbox
                  id="meetsRequirements"
                  checked={declarations.meetsRequirements}
                  onCheckedChange={(checked) => setDeclarations({...declarations, meetsRequirements: checked})}
                  className="mt-0.5"
                />
                <span className="text-sm text-slate-700 leading-relaxed">
                  My <strong>education, skills, and work experience</strong> meet the job requirements.
                </span>
              </label>
              
              {/* Declaration 3 */}
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-50 border border-slate-200">
                <Checkbox
                  id="understandsTerms"
                  checked={declarations.understandsTerms}
                  onCheckedChange={(checked) => setDeclarations({...declarations, understandsTerms: checked})}
                  className="mt-0.5"
                />
                <span className="text-sm text-slate-700 leading-relaxed">
                  I understand that if any information is found to be false, my application may be <strong>rejected</strong> or my employment may be <strong>terminated</strong>.
                </span>
              </label>
              
              {/* Declaration 4 */}
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-50 border border-slate-200">
                <Checkbox
                  id="agreesVerification"
                  checked={declarations.agreesVerification}
                  onCheckedChange={(checked) => setDeclarations({...declarations, agreesVerification: checked})}
                  className="mt-0.5"
                />
                <span className="text-sm text-slate-700 leading-relaxed">
                  I agree that the company may <strong>verify my education, experience, and other details</strong> during the hiring process. By submitting this application, I agree to this declaration.
                </span>
              </label>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowDeclarationModal(false)}
              disabled={applyLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleApply}
              disabled={!allDeclarationsChecked || applyLoading}
              className={`${allDeclarationsChecked ? 'bg-teal-600 hover:bg-teal-700' : 'bg-slate-300'}`}
            >
              {applyLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Agree & Apply
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetails;
